import { Button, CARET_PLACEMENTS, Heading, Text, Tooltip } from '@afterpaytouch/core'
import FormBuilder from '@afterpaytouch/form'
import { useTranslation } from 'next-i18next'
import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react'
import { generateDropdownItems, getExpensechema, orderedDependants, orderedResidentialStatus } from './utils'
import { ExpenseDetailsForm, ExpenseSchemaUpdates } from './types'
import styles from './style.module.css'
import { FacilityExpensesAssessmentRequest, HouseholdDependants, ResidentialStatus } from '@afterpaytouch/portal-api'
import { useAppRouter, useCheckoutToken, useValidateFacilityExpensesMutation } from '../../state'
import { useMoneyHooks, useCreateBankingSession } from './hooks'
import { Route } from '../../config/router'
import { BASE_PATH, app } from '../../env'

export const AffordabilityExpenses: FunctionComponent = () => {
  const { t } = useTranslation()
  const token = useCheckoutToken()
  const PORTAL_URL = app.PORTAL_URL
  const { locale } = useAppRouter()
  const [isFormReady, setIsFormReady] = useState<boolean>(false)
  const [schemaUpdates, setSchemaUpdates] = useState<ExpenseSchemaUpdates>({
    showExtraFields: false,
  })
  const [validateExpenses, { isLoading: isExpensesLoading, isSuccess: isExpensesSuccess }] = useValidateFacilityExpensesMutation()
  const { createBankSession } = useCreateBankingSession()
  const { convertToMoney, symbol } = useMoneyHooks()
  const housingCostsRef = useRef<HTMLDivElement>(null)
  const otherLoansRef = useRef<HTMLDivElement>(null)
  const livingExpensesRef = useRef<HTMLDivElement>(null)
  const [loading, setLoading] = useState(false)
  const [showOtherLoansTooltip, setShowOtherLoansTooltip] = useState(false)
  const [showLivingCostsTooltip, setShowLivingCostsTooltip] = useState(false)
  const [showHousingCostsTooltip, setShowHousingCostsTooltip] = useState(false)
  const testNameSpace = 'affordability-expenses'

  const handleSubmit = async (data: ExpenseDetailsForm): Promise<void> => {
    const { residentialStatus, noOfDependents, monthlyHousingCosts, monthlyLivingExpenses, otherMonthlyLoanRepayments } = data

    const params = {
      token,
      residentialStatus,
      noOfDependents: noOfDependents as HouseholdDependants,
      monthlyHousingCosts: convertToMoney(monthlyHousingCosts ?? ''),
      monthlyLivingExpenses: convertToMoney(monthlyLivingExpenses ?? ''),
      otherMonthlyLoanRepayments: convertToMoney(otherMonthlyLoanRepayments ?? ''),
    } as FacilityExpensesAssessmentRequest
    setLoading(true)
    validateExpenses(params)
  }

  const handleChange = (data: ExpenseDetailsForm): void => {
    setSchemaUpdates((currSchema) => ({
      ...currSchema,
      showExtraFields: data?.residentialStatus !== ('' as ResidentialStatus),
    }))
  }

  const handleStateChange = (isValid: boolean): void => {
    setIsFormReady(isValid)
  }

  const handleIconClicked = (name: string): void => {
    switch (name) {
      case 'otherLoans':
        setShowOtherLoansTooltip((current) => !current)
        break
      case 'livingExpenses':
        setShowLivingCostsTooltip((current) => !current)
        break
      case 'housingCosts':
        setShowHousingCostsTooltip((current) => !current)
        break
    }
  }

  const closeToolTip = (): void => {
    setShowOtherLoansTooltip(false)
    setShowLivingCostsTooltip(false)
    setShowHousingCostsTooltip(false)
  }

  const { schema } = useMemo(() => {
    const residentialStatusOptions = generateDropdownItems(t, orderedResidentialStatus, 'residentialStatus')
    const householdDependantsOptions = generateDropdownItems(t, orderedDependants, 'householdDependants')
    const components = {
      footer: () => {
        return (
          <Button.Primary
            size='Large'
            type='submit'
            disabled={!isFormReady}
            padding='Fluid'
            testNameSpace={testNameSpace}
            loading={isExpensesLoading || loading}
          >
            {t('consumerLending:affordability.expenses.cta')}
          </Button.Primary>
        )
      },
    }
    return getExpensechema(
      components,
      t,
      schemaUpdates,
      residentialStatusOptions,
      householdDependantsOptions,
      handleIconClicked,
      otherLoansRef,
      livingExpensesRef,
      housingCostsRef,
      symbol
    )
  }, [t, schemaUpdates, isFormReady, isExpensesLoading, loading])

  const otherLoansTooltipText = (): JSX.Element => <Text>{t('consumerLending:affordability.otherLoans.tooltip')}</Text>

  const livingExpensesTooltipText = (): JSX.Element => <Text>{t('consumerLending:affordability.livingExpenses.tooltip')}</Text>

  const housingCostsTooltipText = (): JSX.Element => <Text>{t('consumerLending:affordability.housingCosts.tooltip')}</Text>

  useEffect(() => {
    if (isExpensesSuccess) {
      createBankSession({
        onCompleteUrl: `${PORTAL_URL}${BASE_PATH}/${locale}${Route.AFFORDABILITY_VERIFYING}`,
        onCancelUrl: `${PORTAL_URL}${BASE_PATH}/${locale}${Route.PAY_MONTHLY}`,
      })
    }
  }, [isExpensesSuccess])

  return (
    <>
      <Heading size={'XXL'}>{t('consumerLending:affordability.expenses.title')}</Heading>
      <div className={styles.subheading}>
        <Text>{t('consumerLending:affordability.expenses.subtitle')}</Text>
      </div>
      <FormBuilder
        schema={schema}
        testNameSpace={testNameSpace}
        onSubmit={handleSubmit}
        onChange={handleChange}
        disabled={false}
        onStateChange={handleStateChange}
      />
      {showOtherLoansTooltip && (
        <div className={styles.tooltipWrapper}>
          <Tooltip
            id={'other-loans-tooltip'}
            triggerRef={otherLoansRef}
            caretPlacement={CARET_PLACEMENTS.TOP_END}
            testNameSpace={`${testNameSpace}-otherLoans`}
            usePortal={false}
            content={otherLoansTooltipText()}
            hasCaret={true}
            onClose={closeToolTip}
            useMobileView={false}
            bubbleSize={{ width: '100%' }}
            skidding={-15}
            closeOnOutsideClick={closeToolTip}
          />
        </div>
      )}
      {showLivingCostsTooltip && (
        <div className={styles.tooltipWrapper}>
          <Tooltip
            id={'living-expenses-tooltip'}
            triggerRef={livingExpensesRef}
            caretPlacement={CARET_PLACEMENTS.TOP_END}
            testNameSpace={`${testNameSpace}-livingExpenses`}
            usePortal={false}
            content={livingExpensesTooltipText()}
            hasCaret={true}
            onClose={closeToolTip}
            useMobileView={false}
            bubbleSize={{ width: '100%' }}
            skidding={-15}
            closeOnOutsideClick={closeToolTip}
          />
        </div>
      )}
      {showHousingCostsTooltip && (
        <div className={styles.tooltipWrapper}>
          <Tooltip
            id={'housing-costs-tooltip'}
            triggerRef={housingCostsRef}
            caretPlacement={CARET_PLACEMENTS.TOP_END}
            testNameSpace={`${testNameSpace}-housingCosts`}
            usePortal={false}
            content={housingCostsTooltipText()}
            hasCaret={true}
            onClose={closeToolTip}
            useMobileView={false}
            bubbleSize={{ width: '100%' }}
            skidding={-15}
            closeOnOutsideClick={closeToolTip}
          />
        </div>
      )}
    </>
  )
}
